import { STRIPE } from "../constants";

const { ZERO_DECIMAL_CURRENCIES } = STRIPE;
const INTERVAL_MAP = {
  month: {
    1: "Monthly",
    2: "Bimonthly",
    3: "Quarterly",
  },
  week: {
    1: "Weekly",
    2: "Biweekly",
  },
};
const MINIMUM_MAP = {
  Weekly: 24,
  Biweekly: 12,
  Monthly: 6,
  Bimonthly: 3,
  Quarterly: 2,
};
// i18n keys for cadences Stripe allows but that have no one-word name, e.g.
// every 4 weeks. Each takes a {{count}}.
const CUSTOM_INTERVAL_KEYS = {
  day: "everyDays",
  week: "everyWeeks",
  month: "everyMonths",
  year: "everyYears",
};
const FREQUENCY_MAP = {
  month: {
    1: "month",
    2: "bimonthly",
    3: "quarter",
  },
  week: {
    1: "week",
    2: "biweekly",
  },
};

/**
 * Returns the divisor to use to calculate a price (1 if zero-decimal currency, 100 otherwise)
 *
 * @param currency {String} currency of the price
 * @returns {1 | 100} 1 | 100
 */
export const getDivisor = (currency = "usd") => {
  if (ZERO_DECIMAL_CURRENCIES.includes(currency.toUpperCase())) {
    return 1;
  }
  return 100;
};

/**
 * Returns the interval string to display
 *
 * @param currency {Object} recurring object
 * @returns {String} Interval string
 */
export const getIntervalString = (recurring) => {
  if (!recurring) return "";
  const { interval, interval_count } = recurring;
  // Custom cadences (every 4 weeks, every 5 months, …) have no single-word
  // name — describing them is better than calling every-4-weeks "Weekly".
  if (interval_count > 1 && !INTERVAL_MAP[interval]?.[interval_count]) {
    return `Every ${interval_count} ${interval}s`;
  }
  let intervalString = "";
  if (interval === "month") {
    intervalString = INTERVAL_MAP[interval][interval_count] || "Monthly";
  } else if (interval === "year") {
    intervalString = "Annual";
  } else if (interval === "week") {
    intervalString = INTERVAL_MAP[interval][interval_count] || "Weekly";
  } else if (interval === "day") {
    intervalString = "Daily";
  }
  return `${intervalString}`;
};

/**
 * Stable key for i18n, matching {@link getIntervalString} cases.
 *
 * @param recurring {Object | undefined} Stripe recurring object
 * @returns {string | null} e.g. "monthly", "weekly", or null if unknown
 */
export const getStripeIntervalKey = (recurring) => {
  if (!recurring) return null;
  const { interval, interval_count } = recurring;
  // A custom cadence has no single-word name, so it gets a counted key
  // ("Every 4 weeks") rather than being mislabelled as "weekly".
  if (interval_count > 1 && !INTERVAL_MAP[interval]?.[interval_count]) {
    return CUSTOM_INTERVAL_KEYS[interval] || null;
  }
  if (interval === "month") {
    return (
      { 1: "monthly", 2: "bimonthly", 3: "quarterly" }[interval_count] ||
      "monthly"
    );
  }
  if (interval === "year") return "annual";
  if (interval === "week") {
    return { 1: "weekly", 2: "biweekly" }[interval_count] || "weekly";
  }
  if (interval === "day") return "daily";
  return null;
};

/**
 * Translated interval label for display, e.g. "Monthly" or "Every 4 weeks".
 * Falls back to the English {@link getIntervalString} if the cadence has no key.
 *
 * @param recurring {Object | undefined} Stripe recurring object
 * @param t {Function} i18n translator bound to the coaching-portal namespace
 * @returns {string}
 */
export const getIntervalLabel = (recurring, t) => {
  const key = getStripeIntervalKey(recurring);
  if (!key) return getIntervalString(recurring);
  return t(`planManager.stripeIntervalLabels.${key}`, {
    count: recurring?.interval_count,
  });
};

export const getValidityText = (recurring) => {
  const { interval, interval_count } = recurring;
  if (interval === "day") {
    return `${interval_count} Day validity`;
  } else if (interval === "week") {
    return `${interval_count} Week validity`;
  } else if (interval === "year") {
    return `${interval_count} Year validity`;
  } else {
    return `${interval_count} Month validity`;
  }
};

export const getFeatures = (plan) => {
  const features = [];

  if (plan?.metadata) {
    Object.entries(plan.metadata).map(([key, value]) => {
      if (key.startsWith("feature")) {
        features.push(value);
      }
    });
  }
  return features.length > 0 ? features : null;
};

export const getMinimumTermLength = (recurring) => {
  const intervalString = getIntervalString(recurring);
  let length = 1;
  if (intervalString && intervalString !== "Annual") {
    length = MINIMUM_MAP[intervalString] ?? 1;
  }
  return length;
};

export const getFrequencyString = (recurring, periods) => {
  if (!recurring) return "";
  const { interval, interval_count } = recurring;
  let intervalString = "";
  if (interval === "month") {
    intervalString = FREQUENCY_MAP[interval][interval_count] || "month";
  } else if (interval === "year") {
    intervalString = "Annual";
  } else if (interval === "week") {
    intervalString = FREQUENCY_MAP[interval][interval_count] || "week";
  } else if (interval === "day") {
    intervalString = "day";
  }

  return `${intervalString}${periods > 1 ? "s" : ""}`;
};
